import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useLanguage } from "../context/LanguageContext";
import { getBackendUrl } from "../config";

const BACKEND_URL = getBackendUrl();

export default function Control() {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [dataLoggers, setDataLoggers] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDataLogger, setSelectedDataLogger] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [writing, setWriting] = useState(false);
  const [limitMode, setLimitMode] = useState("disabled"); // disabled | percent | kw
  const [limitValue, setLimitValue] = useState("");
  const [reactiveKvar, setReactiveKvar] = useState("");
  const [powerFactor, setPowerFactor] = useState("");
  const [qMode, setQMode] = useState("0"); // 0=disabled, 1=Q, 2=PF, ...
  const [selectedControl, setSelectedControl] = useState("limit"); // limit | reactive | pf | qmode
  const [plantPowerKw, setPlantPowerKw] = useState(null);
  const [inverterPowerMap, setInverterPowerMap] = useState({});
  const isDark = document.documentElement.classList.contains("dark");

  const getPowerKwFromPayload = (payload) => {
    if (!payload || typeof payload !== "object") return null;
    const keys = [
      "active_power_kw",
      "dc_power_kw",
      "pac",
      "power",
      "pv_power",
      "ac_power",
      "output_power",
    ];
    const tryVal = (obj) => {
      if (!obj) return null;
      for (const k of keys) {
        const v = obj[k];
        if (v != null && v !== "") {
          const n = Number(v);
          if (!Number.isNaN(n)) return n;
        }
      }
      if (obj.active_power_mw != null)
        return Number(obj.active_power_mw) * 1000;
      return null;
    };
    const v = tryVal(payload);
    if (v != null) return v;
    for (const val of Object.values(payload)) {
      if (val && typeof val === "object") {
        const r = tryVal(val);
        if (r != null) return r;
      }
    }
    return null;
  };

  const bgCard = isDark ? "bg-[#1e293b]" : "bg-white";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-600";
  const borderColor = isDark ? "border-slate-700" : "border-gray-200";

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(`${BACKEND_URL}/api/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompanies(res.data || []);
        if (res.data?.length > 0) {
          setSelectedCompany((prev) => prev ?? res.data[0]);
        }
      } catch (e) {
        console.error("Error fetching companies:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    setSelectedDataLogger(null);
    setInverters([]);
    setSelectedIds(new Set());
    if (!selectedCompany?.id) return;
    const fetchDataLoggers = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(
          `${BACKEND_URL}/api/devices/data-loggers?company_id=${encodeURIComponent(selectedCompany.id)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const list = res.data || [];
        setDataLoggers(list);
        if (list.length > 0) setSelectedDataLogger(list[0]);
      } catch (e) {
        console.error("Error fetching data loggers:", e);
      }
    };
    fetchDataLoggers();
  }, [selectedCompany?.id]);

  useEffect(() => {
    setSelectedIds(new Set());
    setPlantPowerKw(null);
    setInverterPowerMap({});
    if (!selectedDataLogger?.id) {
      setInverters([]);
      return;
    }
    const fetchInverters = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(
          `${BACKEND_URL}/api/devices/inverters?company_id=${encodeURIComponent(selectedCompany?.id || "")}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const list = (res.data || []).filter(
          (inv) => String(inv.data_logger_id) === String(selectedDataLogger.id),
        );
        setInverters(list);
      } catch (e) {
        console.error("Error fetching inverters:", e);
        setInverters([]);
      }
    };
    fetchInverters();
  }, [selectedCompany?.id, selectedDataLogger?.id]);

  // Prodhimi aktual (kW) – datalogger reading
  useEffect(() => {
    if (!selectedDataLogger?.id) {
      setPlantPowerKw(null);
      return;
    }
    const fetchPlantPower = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(
          `${BACKEND_URL}/api/devices/${selectedDataLogger.id}/pi-datalogger-readings?limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const readings = res?.data?.readings || [];
        const row = readings[0];
        const pl = row?.payload || row?.display_payload;
        const kw = getPowerKwFromPayload(pl);
        setPlantPowerKw(kw != null ? Math.round(kw * 10) / 10 : null);
      } catch (e) {
        setPlantPowerKw(null);
      }
    };
    fetchPlantPower();
    const iv = setInterval(fetchPlantPower, 30000);
    return () => clearInterval(iv);
  }, [selectedDataLogger?.id]);

  // Fuqia për çdo inverter (kW)
  useEffect(() => {
    const list = inverters || [];
    if (!list.length) {
      setInverterPowerMap({});
      return;
    }
    const token = localStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all(
      list.map((inv) =>
        axios
          .get(
            `${BACKEND_URL}/api/devices/${inv.id}/pi-inverter-readings?limit=1`,
            { headers },
          )
          .then((r) => {
            const readings = r?.data?.readings || [];
            const row = readings[0];
            const pl = row?.payload || row?.display_payload;
            const kw = getPowerKwFromPayload(pl);
            return {
              id: inv.id,
              kw: kw != null ? Math.round(kw * 10) / 10 : null,
            };
          })
          .catch(() => ({ id: inv.id, kw: null })),
      ),
    ).then((results) => {
      const map = {};
      results.forEach((r) => {
        map[r.id] = r.kw;
      });
      setInverterPowerMap(map);
    });
  }, [selectedDataLogger?.id, inverters]);

  const invList = inverters || [];
  const allIds = invList.map((i) => i.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const openConfirm = (command, label) => {
    setConfirmModal({ command, label });
    setPassword("");
    setConfirmed(false);
  };

  const closeConfirm = () => {
    setConfirmModal(null);
    setPassword("");
    setConfirmed(false);
  };

  const handleExecute = async () => {
    if (!confirmModal || !password || !confirmed || !selectedDataLogger?.id)
      return;
    setWriting(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(
        `${BACKEND_URL}/api/devices/data-loggers/${selectedDataLogger.id}/write-control`,
        { password, confirmed, command: confirmModal.command },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      closeConfirm();
      const s = res?.data?.status;
      const isLimit = confirmModal.command?.startsWith("limit_");
      const isSetpoint =
        confirmModal.command?.startsWith("reactive_kvar:") ||
        confirmModal.command?.startsWith("power_factor:") ||
        confirmModal.command?.startsWith("q_mode:");
      const cmdLabel =
        {
          inverter_start: "Start",
          inverter_stop: "Stop",
          plant_shutdown: "Shutdown Plant",
          plant_enable: "Enable Plant",
          limit_disable: "Hiqe limitin",
        }[confirmModal.command] ||
        confirmModal.label ||
        confirmModal.command;
      let msg = isLimit
        ? `U krye!\n\nLimit u aplikua: ${cmdLabel}`
        : isSetpoint
          ? `U krye!\n\n${cmdLabel}`
          : `Komanda u ekzekutua: ${cmdLabel}`;
      if (s?.inverter_control_text) msg += `\n${s.inverter_control_text}`;
      if (s?.plant_status_text) msg += `\n${s.plant_status_text}`;
      toast.error(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Gabim");
    } finally {
      setWriting(false);
    }
  };

  const getConfirmMessage = (cmd) => {
    if (cmd === "inverter_stop")
      return "A je i sigurt? Dëshiron të ndalësh inverterat e zgjedhur?";
    if (cmd === "inverter_start")
      return "A je i sigurt? Dëshiron të ndezësh inverterat e zgjedhur?";
    if (cmd === "plant_shutdown")
      return "A je i sigurt? Dëshiron të ndalësh të gjithë plantin?";
    if (cmd === "plant_enable")
      return "A je i sigurt? Dëshiron të ndezësh/aktivizosh plantin?";
    if (cmd === "limit_disable")
      return "A je i sigurt? Dëshiron të hiqësh limitin e prodhimit?";
    if (cmd?.startsWith("limit_percent:"))
      return "A je i sigurt? Do të aplikosh limit me %?";
    if (cmd?.startsWith("limit_kw:"))
      return "A je i sigurt? Do të aplikosh limit me kW?";
    if (cmd?.startsWith("reactive_kvar:"))
      return "A je i sigurt? Do të vendosësh Q (reaktiv) në kVar?";
    if (cmd?.startsWith("power_factor:"))
      return "A je i sigurt? Do të vendosësh Power Factor (cosφ)?";
    if (cmd?.startsWith("q_mode:"))
      return "A je i sigurt? Do të ndërrosh mënyrën e kontrollit reaktiv?";
    return "A je i sigurt?";
  };

  const openLimitConfirm = () => {
    if (!selectedDataLogger?.id) {
      toast.error("Zgjidhni kompaninë dhe data logger.");
      return;
    }
    let command, label;
    if (limitMode === "disabled") {
      command = "limit_disable";
      label = "Hiqe limitin";
    } else if (limitMode === "percent") {
      const pct = parseFloat(limitValue);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        toast.error("Vendosni % të vlefshme (0–100).");
        return;
      }
      command = `limit_percent:${pct}`;
      label = `Limit ${pct}%`;
    } else {
      const kw = parseFloat(limitValue);
      if (isNaN(kw) || kw < 0) {
        toast.error("Vendosni kW të vlefshme.");
        return;
      }
      command = `limit_kw:${kw}`;
      label = `Limit ${kw} kW`;
    }
    setConfirmModal({ command, label });
    setPassword("");
    setConfirmed(false);
  };

  const openReactiveConfirm = () => {
    if (!selectedDataLogger?.id) {
      toast.error("Zgjidhni kompaninë dhe data logger.");
      return;
    }
    const kvar = parseFloat(reactiveKvar);
    if (isNaN(kvar)) {
      toast.error("Vendosni vlerë kVar të vlefshme (p.sh. -50, 0, +50).");
      return;
    }
    setConfirmModal({
      command: `reactive_kvar:${kvar}`,
      label: `Reactive ${kvar} kVar`,
    });
    setPassword("");
    setConfirmed(false);
  };

  const openPfConfirm = () => {
    if (!selectedDataLogger?.id) {
      toast.error("Zgjidhni kompaninë dhe data logger.");
      return;
    }
    const pf = parseFloat(powerFactor);
    if (isNaN(pf) || pf < -1 || pf > 1) {
      toast.error("Vendosni PF të vlefshme (0.80–1.00 ose -1.00–1.00).");
      return;
    }
    setConfirmModal({
      command: `power_factor:${pf}`,
      label: `Power Factor ${pf}`,
    });
    setPassword("");
    setConfirmed(false);
  };

  const Q_MODE_LABELS = {
    0: "Disabled",
    1: "Q (kVar)",
    2: "PF (cosφ)",
    3: "Volt-Var",
    4: "Volt-Watt",
  };
  const openQModeConfirm = () => {
    if (!selectedDataLogger?.id) {
      toast.error("Zgjidhni kompaninë dhe data logger.");
      return;
    }
    const mode = parseInt(qMode, 10);
    if (isNaN(mode) || mode < 0 || mode > 255) {
      toast.error("Zgjidhni mënyrë të vlefshme.");
      return;
    }
    const modeLabel = Q_MODE_LABELS[mode] || `Mode ${mode}`;
    setConfirmModal({
      command: `q_mode:${mode}`,
      label: `Mënyra reaktive: ${modeLabel}`,
    });
    setPassword("");
    setConfirmed(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-gray-500 dark:text-gray-400">{t("loading")}</p>
        </div>
      </Layout>
    );
  }

  const CONTROL_OPTIONS = [
    { value: "limit", labelKey: "limitProduction" },
    { value: "reactive", labelKey: "reactivePower" },
    { value: "pf", labelKey: "powerFactor" },
    { value: "qmode", labelKey: "reactiveMode" },
  ];

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">
          {t("controlDatalogger")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 md:mb-6">
          {t("controlSubtitle")}
        </p>

        {/* Web: horizontal 3 columns */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-4 md:gap-5 mb-6">
          {/* Col 1: Kompani + Data Logger */}
          <div
            className={`${bgCard} rounded-xl border p-4 ${borderColor} flex flex-col gap-3`}
          >
            <label className={`block text-xs font-medium ${textSecondary}`}>
              {t("company")}
            </label>
            <select
              value={selectedCompany?.id || ""}
              onChange={(e) => {
                const c = companies.find(
                  (x) => String(x.id) === String(e.target.value),
                );
                setSelectedCompany(c || null);
              }}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              <option value="">{t("selectCompany")}</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <label className={`block text-xs font-medium ${textSecondary}`}>
              {t("dataLogger")}
            </label>
            <select
              value={selectedDataLogger?.id || ""}
              onChange={(e) => {
                const dl = dataLoggers.find(
                  (x) => String(x.id) === String(e.target.value),
                );
                setSelectedDataLogger(dl || null);
              }}
              disabled={!selectedCompany}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"} disabled:opacity-50`}
            >
              <option value="">{t("selectDataLogger")}</option>
              {dataLoggers.map((dl) => (
                <option key={dl.id} value={dl.id}>
                  {dl.model || dl.name || dl.serial_number || dl.id}
                </option>
              ))}
            </select>
          </div>

          {/* Col 2: Veprimet – Start/Stop lart, Shutdown/Enable poshtë me hapësirë */}
          <div className={`${bgCard} rounded-xl border p-4 ${borderColor}`}>
            <label
              className={`block text-xs font-medium mb-2 ${textSecondary}`}
            >
              {t("actions")}
            </label>
            <div className="flex flex-col gap-1">
              <div className="flex gap-4 justify-between">
                <button
                  onClick={() => openConfirm("inverter_start", "Start")}
                  disabled={invList.length === 0}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white transition"
                >
                  {t("start")}
                </button>
                <button
                  onClick={() => openConfirm("inverter_stop", "Stop")}
                  disabled={invList.length === 0}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white transition"
                >
                  {t("stop")}
                </button>
              </div>
              <div className="flex gap-2 justify-between mt-4">
                <button
                  onClick={() => openConfirm("plant_shutdown", "Shutdown")}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition"
                >
                  {t("shutdown")}
                </button>
                <button
                  onClick={() => openConfirm("plant_enable", "Enable")}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white transition"
                >
                  {t("enable")}
                </button>
              </div>
            </div>
          </div>

          {/* Col 3: Kufizimi prodhimit + dropdown për kontrollet */}
          <div className={`${bgCard} rounded-xl border p-4 ${borderColor}`}>
            <label
              className={`block text-xs font-medium mb-2 ${textSecondary}`}
            >
              {t("controlLabel")}
            </label>
            <select
              value={selectedControl}
              onChange={(e) => setSelectedControl(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm mb-4 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              {CONTROL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.labelKey ? t(o.labelKey) : o.label}
                </option>
              ))}
            </select>

            {/* Form sipas kontrolit të zgjedhur */}
            {selectedControl === "limit" && (
              <>
                {plantPowerKw != null && (
                  <p className={`text-xs font-medium mb-2 ${textPrimary}`}>
                    {t("current")}:{" "}
                    <span className="text-green-600 dark:text-green-400">
                      {plantPowerKw} kW
                    </span>
                  </p>
                )}
                <select
                  value={limitMode}
                  onChange={(e) => setLimitMode(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="disabled">{t("disabled")}</option>
                  <option value="percent">{t("percent")}</option>
                  <option value="kw">{t("kw")}</option>
                </select>
                {(limitMode === "percent" || limitMode === "kw") && (
                  <input
                    type="number"
                    min={limitMode === "percent" ? 0 : 0}
                    max={limitMode === "percent" ? 100 : undefined}
                    step={limitMode === "percent" ? 1 : 0.1}
                    placeholder={limitMode === "percent" ? "60" : "800"}
                    value={limitValue}
                    onChange={(e) => setLimitValue(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  />
                )}
                <button
                  onClick={openLimitConfirm}
                  disabled={
                    !selectedDataLogger ||
                    (limitMode !== "disabled" && !limitValue)
                  }
                  className="w-full px-3 py-2 rounded-lg text-xs font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white"
                >
                  {t("apply")}
                </button>
              </>
            )}
            {selectedControl === "reactive" && (
              <>
                <input
                  type="number"
                  step="1"
                  placeholder="kVar (p.sh. -50, +50)"
                  value={reactiveKvar}
                  onChange={(e) => setReactiveKvar(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <button
                  onClick={openReactiveConfirm}
                  disabled={!selectedDataLogger || !reactiveKvar}
                  className="w-full px-3 py-2 rounded-lg text-xs font-medium bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white"
                >
                  {t("apply")} Q
                </button>
              </>
            )}
            {selectedControl === "pf" && (
              <>
                <input
                  type="number"
                  min="-1"
                  max="1"
                  step="0.01"
                  placeholder="PF 0.95"
                  value={powerFactor}
                  onChange={(e) => setPowerFactor(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <button
                  onClick={openPfConfirm}
                  disabled={!selectedDataLogger || !powerFactor}
                  className="w-full px-3 py-2 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white"
                >
                  {t("apply")} PF
                </button>
              </>
            )}
            {selectedControl === "qmode" && (
              <>
                <select
                  value={qMode}
                  onChange={(e) => setQMode(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="0">{t("disabled")}</option>
                  <option value="1">{t("qKvar")}</option>
                  <option value="2">{t("pfCos")}</option>
                  <option value="3">{t("voltVar")}</option>
                  <option value="4">{t("voltWatt")}</option>
                </select>
                <button
                  onClick={openQModeConfirm}
                  disabled={!selectedDataLogger}
                  className="w-full px-3 py-2 rounded-lg text-xs font-medium bg-slate-600 hover:bg-slate-700 disabled:opacity-50 text-white"
                >
                  {t("apply")} mënyrën
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile: stacked, butona të vegjël me hapësirë, kontrollet me dropdown */}
        <div className="lg:hidden space-y-4">
          <div className={`${bgCard} rounded-xl border p-4 ${borderColor}`}>
            <label
              className={`block text-xs font-medium mb-2 ${textSecondary}`}
            >
              {t("company")}
            </label>
            <select
              value={selectedCompany?.id || ""}
              onChange={(e) => {
                const c = companies.find(
                  (x) => String(x.id) === String(e.target.value),
                );
                setSelectedCompany(c || null);
              }}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              <option value="">{t("selectCompany")}</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <label
              className={`block text-xs font-medium mt-3 mb-2 ${textSecondary}`}
            >
              {t("dataLogger")}
            </label>
            <select
              value={selectedDataLogger?.id || ""}
              onChange={(e) => {
                const dl = dataLoggers.find(
                  (x) => String(x.id) === String(e.target.value),
                );
                setSelectedDataLogger(dl || null);
              }}
              disabled={!selectedCompany}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"} disabled:opacity-50`}
            >
              <option value="">{t("selectDataLogger")}</option>
              {dataLoggers.map((dl) => (
                <option key={dl.id} value={dl.id}>
                  {dl.model || dl.name || dl.serial_number || dl.id}
                </option>
              ))}
            </select>
          </div>

          <div className={`${bgCard} rounded-xl border p-4 ${borderColor}`}>
            <label
              className={`block text-xs font-medium mb-3 ${textSecondary}`}
            >
              {t("actions")}
            </label>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => openConfirm("inverter_start", "Start")}
                disabled={invList.length === 0}
                className="px-3 py-2 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white"
              >
                {t("start")}
              </button>
              <button
                onClick={() => openConfirm("inverter_stop", "Stop")}
                disabled={invList.length === 0}
                className="px-3 py-2 rounded-lg text-xs font-medium bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white"
              >
                {t("stop")}
              </button>
              <button
                onClick={() => openConfirm("plant_shutdown", "Shutdown")}
                className="px-3 py-2 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white"
              >
                {t("shutdown")}
              </button>
              <button
                onClick={() => openConfirm("plant_enable", "Enable")}
                className="px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t("enable")}
              </button>
            </div>
          </div>

          <div className={`${bgCard} rounded-xl border p-4 ${borderColor}`}>
            <label
              className={`block text-xs font-medium mb-2 ${textSecondary}`}
            >
              {t("controlLabel")}
            </label>
            <select
              value={selectedControl}
              onChange={(e) => setSelectedControl(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm mb-4 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              {CONTROL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.labelKey ? t(o.labelKey) : o.label}
                </option>
              ))}
            </select>
            {selectedControl === "limit" && (
              <>
                {plantPowerKw != null && (
                  <p className={`text-xs mb-2 ${textPrimary}`}>
                    Aktual:{" "}
                    <span className="text-green-600">{plantPowerKw} kW</span>
                  </p>
                )}
                <select
                  value={limitMode}
                  onChange={(e) => setLimitMode(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="disabled">{t("disabled")}</option>
                  <option value="percent">{t("percent")}</option>
                  <option value="kw">{t("kw")}</option>
                </select>
                {(limitMode === "percent" || limitMode === "kw") && (
                  <input
                    type="number"
                    placeholder={limitMode === "percent" ? "60" : "800"}
                    value={limitValue}
                    onChange={(e) => setLimitValue(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  />
                )}
                <button
                  onClick={openLimitConfirm}
                  disabled={
                    !selectedDataLogger ||
                    (limitMode !== "disabled" && !limitValue)
                  }
                  className="w-full py-2 rounded-lg text-xs font-medium bg-purple-600 text-white disabled:opacity-50"
                >
                  Apliko
                </button>
              </>
            )}
            {selectedControl === "reactive" && (
              <>
                <input
                  type="number"
                  placeholder="kVar"
                  value={reactiveKvar}
                  onChange={(e) => setReactiveKvar(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <button
                  onClick={openReactiveConfirm}
                  disabled={!selectedDataLogger || !reactiveKvar}
                  className="w-full py-2 rounded-lg text-xs font-medium bg-teal-600 text-white disabled:opacity-50"
                >
                  {t("applyQ")}
                </button>
              </>
            )}
            {selectedControl === "pf" && (
              <>
                <input
                  type="number"
                  placeholder="PF 0.95"
                  value={powerFactor}
                  onChange={(e) => setPowerFactor(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <button
                  onClick={openPfConfirm}
                  disabled={!selectedDataLogger || !powerFactor}
                  className="w-full py-2 rounded-lg text-xs font-medium bg-indigo-600 text-white disabled:opacity-50"
                >
                  {t("applyPF")}
                </button>
              </>
            )}
            {selectedControl === "qmode" && (
              <>
                <select
                  value={qMode}
                  onChange={(e) => setQMode(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="0">{t("disabled")}</option>
                  <option value="1">{t("qKvar")}</option>
                  <option value="2">{t("pfCos")}</option>
                  <option value="3">{t("voltVar")}</option>
                  <option value="4">{t("voltWatt")}</option>
                </select>
                <button
                  onClick={openQModeConfirm}
                  disabled={!selectedDataLogger}
                  className="w-full py-2 rounded-lg text-xs font-medium bg-slate-600 text-white disabled:opacity-50"
                >
                  {t("applyMode")}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Lista inverterëve – çdo inverter në rreshtin e vet */}
        <div className={`${bgCard} rounded-xl border p-4 ${borderColor} mt-6`}>
          <h3 className={`font-semibold text-sm ${textPrimary} mb-2`}>
            {t("inverterList")}
          </h3>
          <div className={`rounded-lg border p-3 ${borderColor}`}>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="rounded"
              />
              <span className={`text-sm ${textPrimary}`}>{t("selectAll")}</span>
            </label>
            <div className="flex flex-col gap-1">
              {invList.length === 0 ? (
                <p className={`text-sm ${textSecondary}`}>
                  {selectedDataLogger
                    ? t("noInverters")
                    : t("selectDataLoggerFirst")}
                </p>
              ) : (
                invList.map((inv) => (
                  <label
                    key={inv.id}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(inv.id)}
                      onChange={() => toggleOne(inv.id)}
                      className="rounded"
                    />
                    <span className={textPrimary}>
                      {inv.name || inv.model || inv.serial_number || inv.id}
                    </span>
                    {inverterPowerMap[inv.id] != null && (
                      <span className={textSecondary}>
                        ({inverterPowerMap[inv.id]} kW)
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-2xl p-5 w-full max-w-sm ${isDark ? "bg-slate-800" : "bg-white"} shadow-xl`}
          >
            <h3 className={`font-bold mb-2 ${textPrimary}`}>
              {confirmModal.label}
            </h3>
            <p className={`text-sm mb-3 ${textSecondary}`}>
              {getConfirmMessage(confirmModal.command)}
            </p>
            <input
              type="password"
              placeholder={t("password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border mb-2 ${isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-gray-300"}`}
            />
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="rounded"
              />
              <span className={textSecondary}>{t("confirmCheck")}</span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={closeConfirm}
                className={`flex-1 py-2 rounded-lg ${isDark ? "bg-slate-600 text-white" : "bg-gray-200 text-gray-700"}`}
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleExecute}
                disabled={writing || !password || !confirmed}
                className="flex-1 py-2 rounded-lg bg-purple-600 text-white disabled:opacity-50"
              >
                {writing ? t("sending") : t("execute")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
